
import React from 'react';
import { PopularResultsCard } from './components/PopularResultsCard';
import { CommonResultsCard } from './components/CommonResultsCard';
import { GoalsStatsCard } from './components/GoalsStatsCard';
import { FoulsSummaryCard } from './components/FoulsSummaryCard';
import { ShotsStatsCard } from './components/ShotsStatsCard';
import { TeamStats } from '../types/head-to-head';
import { Match } from '../types/head-to-head';

interface HeadToHeadStatsProps {
  homeTeamStats: TeamStats;
  awayTeamStats: TeamStats;
  homeTeam?: string;
  awayTeam?: string;
  matches: Match[];
}

const HeadToHeadStats: React.FC<HeadToHeadStatsProps> = ({
  homeTeamStats,
  awayTeamStats,
  homeTeam = 'Home Team',
  awayTeam = 'Away Team',
  matches
}) => {
  const goalThresholds = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
  const teamGoalsStats = goalThresholds.map(threshold => ({
    label: `>${threshold}`,
    homeValue: homeTeamStats.teamGoalsOver?.[threshold] || 0,
    awayValue: awayTeamStats.teamGoalsOver?.[threshold] || 0,
    total: homeTeamStats.played
  }));

  const halfTimeGoalsStats = goalThresholds.map(threshold => ({
    label: `>${threshold}`,
    homeValue: homeTeamStats.halfTimeTeamGoalsOver?.[threshold] || 0,
    awayValue: awayTeamStats.halfTimeTeamGoalsOver?.[threshold] || 0,
    total: homeTeamStats.played
  }));

  const cornerThresholds = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5, 6.5, 7.5, 8.5, 9.5, 10.5, 11.5, 12.5];
  const teamCornersStats = cornerThresholds.map(threshold => ({
    label: `>${threshold}`,
    homeValue: homeTeamStats.teamCornersOver?.[threshold] || 0,
    awayValue: awayTeamStats.teamCornersOver?.[threshold] || 0,
    total: homeTeamStats.played
  }));

  const cardThresholds = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
  const teamCardsStats = cardThresholds.map(threshold => ({
    label: `>${threshold}`,
    homeValue: homeTeamStats.teamCardsOver?.[threshold] || 0,
    awayValue: awayTeamStats.teamCardsOver?.[threshold] || 0,
    total: homeTeamStats.played
  }));

  // Calculate half-time stats for both teams
  const calculateTeamHalfTimeStats = (team: string) => {
    let stats = {
      leading: { matches: 0, winFt: 0, drawFt: 0, lossFt: 0 },
      drawing: { matches: 0, winFt: 0, drawFt: 0, lossFt: 0 },
      losing: { matches: 0, winFt: 0, drawFt: 0, lossFt: 0 }
    };

    matches.forEach(match => {
      const isTeamHome = match.HomeTeam === team;
      const homeGoals = parseInt(match.HTHG) || 0;
      const awayGoals = parseInt(match.HTAG) || 0;
      const ftHomeGoals = parseInt(match.FTHG) || 0;
      const ftAwayGoals = parseInt(match.FTAG) || 0;

      // Determine half-time state from team's perspective
      const htGoalDiff = isTeamHome ? homeGoals - awayGoals : awayGoals - homeGoals;
      const ftWon = isTeamHome ? ftHomeGoals > ftAwayGoals : ftAwayGoals > ftHomeGoals;
      const ftDraw = ftHomeGoals === ftAwayGoals;

      if (htGoalDiff > 0) {
        // Team leading at HT
        stats.leading.matches++;
        if (ftWon) stats.leading.winFt++;
        else if (ftDraw) stats.leading.drawFt++;
        else stats.leading.lossFt++;
      } else if (htGoalDiff === 0) {
        // Team drawing at HT
        stats.drawing.matches++;
        if (ftWon) stats.drawing.winFt++;
        else if (ftDraw) stats.drawing.drawFt++;
        else stats.drawing.lossFt++;
      } else {
        // Team losing at HT
        stats.losing.matches++;
        if (ftWon) stats.losing.winFt++;
        else if (ftDraw) stats.losing.drawFt++;
        else stats.losing.lossFt++;
      }
    });

    return stats;
  };

  const homeHtStats = calculateTeamHalfTimeStats(homeTeam);
  const awayHtStats = calculateTeamHalfTimeStats(awayTeam);

  // Create stats arrays for the cards
  const leadingStats = [
    { label: 'Win', homeValue: homeHtStats.leading.winFt, awayValue: awayHtStats.leading.winFt, total: matches.length },
    { label: 'Draw', homeValue: homeHtStats.leading.drawFt, awayValue: awayHtStats.leading.drawFt, total: matches.length },
    { label: 'Loss', homeValue: homeHtStats.leading.lossFt, awayValue: awayHtStats.leading.lossFt, total: matches.length }
  ];

  const drawingStats = [
    { label: 'Win', homeValue: homeHtStats.drawing.winFt, awayValue: awayHtStats.drawing.winFt, total: matches.length },
    { label: 'Draw', homeValue: homeHtStats.drawing.drawFt, awayValue: awayHtStats.drawing.drawFt, total: matches.length },
    { label: 'Loss', homeValue: homeHtStats.drawing.lossFt, awayValue: awayHtStats.drawing.lossFt, total: matches.length }
  ];

  const losingStats = [
    { label: 'Win', homeValue: homeHtStats.losing.winFt, awayValue: awayHtStats.losing.winFt, total: matches.length },
    { label: 'Draw', homeValue: homeHtStats.losing.drawFt, awayValue: awayHtStats.losing.drawFt, total: matches.length },
    { label: 'Loss', homeValue: homeHtStats.losing.lossFt, awayValue: awayHtStats.losing.lossFt, total: matches.length }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PopularResultsCard
          homeTeamStats={homeTeamStats}
          awayTeamStats={awayTeamStats}
        />
        <CommonResultsCard matches={matches} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GoalsStatsCard
          title="Leading at HT"
          titleDescription={`${homeTeam}: ${homeHtStats.leading.matches} matches, ${awayTeam}: ${awayHtStats.leading.matches} matches`}
          stats={leadingStats}
        />
        <GoalsStatsCard
          title="Drawing at HT"
          titleDescription={`${homeTeam}: ${homeHtStats.drawing.matches} matches, ${awayTeam}: ${awayHtStats.drawing.matches} matches`}
          stats={drawingStats}
        />
        <GoalsStatsCard
          title="Losing at HT"
          titleDescription={`${homeTeam}: ${homeHtStats.losing.matches} matches, ${awayTeam}: ${awayHtStats.losing.matches} matches`}
          stats={losingStats}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <GoalsStatsCard title="Team Goals" stats={teamGoalsStats} />
        <GoalsStatsCard title="Half Time Team Goals" stats={halfTimeGoalsStats} />
        <GoalsStatsCard title="Team Corners" stats={teamCornersStats} />
        <GoalsStatsCard title="Team Cards" stats={teamCardsStats} />
        <FoulsSummaryCard matches={matches} />
        <ShotsStatsCard matches={matches} homeTeam={homeTeam} awayTeam={awayTeam} />
      </div>
    </div>
  );
};

export default HeadToHeadStats;
